import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { utils, writeFile } from 'xlsx';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const DailyStats = () => {
  const [stats, setStats] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }).slice(0, 7) // Default to current month in Mexico City time
  );
  const [allLoadTimes, setAllLoadTimes] = useState([]);
  const [allConversations, setAllConversations] = useState([]);
  const [allTags, setAllTags] = useState([]);

  const formatToMexicoCityTime = (timestamp) => {
    return new Date(
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Mexico_City',
      }).format(new Date(timestamp.seconds * 1000))
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      const loadTimesRef = collection(db, 'loadTimes');
      const conversationsRef = collection(db, 'conversations');
      const tagsRef = collection(db, 'tags');

      const [loadTimesSnapshot, conversationsSnapshot, tagsSnapshot] = await Promise.all([
        getDocs(loadTimesRef),
        getDocs(conversationsRef),
        getDocs(tagsRef),
      ]);

      setAllLoadTimes(loadTimesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setAllConversations(conversationsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setAllTags(tagsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };

    fetchData();
  }, []);

  
  useEffect(() => {
    // Helper functions defined inside the effect
    const hasGclidParam = (url) => {
      try {
        const urlObj = new URL(url);
        return urlObj.searchParams.has('gclid');
      } catch (e) {
        return false;
      }
    };
  
    const determineOrigin = (conversation, loads, tags) => {
      // Check conversation gclid
      if (conversation.gclid) return "ads";
  
      // Check loads for gclid param
      if (loads && loads.some(load => hasGclidParam(load.fullUrl))) {
        return "ads";
      }
  
      return tags.includes("internal") ? "internal" : "organic";
    };
  
    const getConversationDate = (conversation, loads) => {
      let dateObj;
      if (conversation.timestamp) {
        dateObj = formatToMexicoCityTime(conversation.timestamp);
      } else if (loads && loads.length > 0) {
        const earliestLoad = loads.reduce((earliest, l) => {
          return (earliest.timestamp.seconds < l.timestamp.seconds) ? earliest : l;
        });
        dateObj = formatToMexicoCityTime(earliestLoad.timestamp);
      } else {
        // No timestamp at all - skip this conversation
        return null;
      }
  
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
  
    const isErrorTag = (tag) => tag.startsWith("E_");
  
    const analyzeTags = (tags) => {
      const hasFocus = tags.includes("focus");
      const hasCotiza = tags.includes("cotiza");
      const hasWa = tags.includes("WA");
      const hasFinalizado = tags.includes("finalizado");
      const hasError = tags.some(isErrorTag);
      return { hasFocus, hasCotiza, hasWa, hasError, hasFinalizado };
    };

    const processDocuments = (documents, allTags) => {
  
      const resultMap = new Map();
    
      documents.forEach((doc) => {
        // Extract fields from the document
        const { conversationId, deviceType, fullUrl, timestamp } = doc;
    
        // Format the timestamp to a date
        const dateObj = formatToMexicoCityTime(timestamp);
  
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const date = `${year}-${month}-${day}`;
    
        // Determine the origin
        let origin = 'organic';
        if (fullUrl?.includes('gclid=')) {
          origin = 'ads';
        } else if (
          allTags.some(
            (tagDoc) =>
              tagDoc.id === conversationId && tagDoc.tags.includes('internal')
          )
        ) {
          origin = 'internal';
        }
    
        // Generate a unique key for the group
        const key = `${date}|${deviceType}|${origin}`;
    
        // Initialize or update the map
        if (!resultMap.has(key)) {
          resultMap.set(key, {
            date,
            deviceType,
            origin,
            ids: new Set(),
            loads: 0,
          });
        }
    
        const entry = resultMap.get(key);
        if (!entry.ids.has(conversationId)) {
          entry.ids.add(conversationId);
        }
        entry.loads++;
      });
    
      return resultMap
    };
  
    // Create a map for tags by conversation_id
    const tagsByConversation = allTags.reduce((acc, tagDoc) => {
      acc[tagDoc.conversation_id] = tagDoc.tags || [];
      return acc;
    }, {});
  
    // Group loads by conversationId
    const loadsByConversation = allLoadTimes.reduce((acc, loadDoc) => {
      if (!acc[loadDoc.conversationId]) acc[loadDoc.conversationId] = [];
      acc[loadDoc.conversationId].push(loadDoc);
      return acc;
    }, {});
  
    const results = {};
  
    if (allLoadTimes && allConversations && allTags) {
      // Use processDocuments to analyze and extract loads per device type, date, and origin
      const processedData = processDocuments(allLoadTimes, allTags);
    
      for (const conversation of allConversations) {
        const conversationId = conversation.id; // Assuming .id is the identifier
        const tags = tagsByConversation[conversationId] || [];
        const { hasFocus, hasCotiza, hasWa, hasFinalizado, hasError } = analyzeTags(tags);
    
        const loads = loadsByConversation[conversationId] || [];
        // If you still want to skip conversations with no loads, uncomment:
        // if (loads.length === 0) {
        //   continue;
        // }
    
        const date = getConversationDate(conversation, loads);
        if (!date) continue;
    
        const origin = determineOrigin(conversation, loads, tags);
    
        // Extract unique device types
        const deviceTypes = [...new Set(loads.map((l) => l.deviceType))];
    
        for (const deviceType of deviceTypes) {
          const key = `${date}|${deviceType}|${origin}`;
          if (!results[key]) {
            results[key] = {
              date,
              deviceType,
              origin,
              loads: 0,
              writeFocus: 0,
              conversations: 0,
              cotiza: 0,
              wa: 0,
              finalizado: 0,
              errors: 0,
              countedConversationIds: new Set(),
            };
          }
    
          if (!results[key].countedConversationIds.has(conversationId)) {
            results[key].countedConversationIds.add(conversationId);
        
            // Increment other counters
            results[key].conversations += 1; // We have a valid conversation
            if (hasFocus) results[key].writeFocus += 1;
            if (hasCotiza) results[key].cotiza += 1;
            if (hasWa) results[key].wa += 1;
            if (hasFinalizado) results[key].finalizado += 1;
            if (hasError) results[key].errors += 1;
          }
        }
      }

      // Assuming processedData is a Map and results is an object
      for (const [key, value] of processedData) {
        if (results[key]) {
          results[key].loads = results[key].loads || 0;

          // Add the length of the `ids` array in `processedData` to `results[key].loads`
          if (value.loads) {
            results[key].loads += value.loads;
          }
        }
      }
    
      let data = Object.values(results).map((row) => {
        const {
          date,
          deviceType,
          origin,
          loads,
          writeFocus,
          conversations,
          cotiza,
          wa,
          finalizado,
          errors,
        } = row;
        return { date, deviceType, origin, loads, writeFocus, conversations, cotiza, wa, finalizado, errors };
      });
  
      // Filter by selectedMonth if needed
      if (selectedMonth) {
        data = data.filter(item => item.date.startsWith(selectedMonth));
      }
  
      // Sort by date descending
      data.sort((a, b) => new Date(b.date) - new Date(a.date));
  
      console.log(data);
      setStats(data);
    }
  
  }, [selectedMonth, allLoadTimes, allConversations, allTags]);
  

  const handleDownloadExcel = () => {
    const ws = utils.json_to_sheet(stats);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Daily Stats');
    writeFile(wb, `Daily_Stats_${selectedMonth}.xlsx`);
  };

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-4xl mx-auto">
      <h3 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">Monthly Statistics</h3>

      <div className="flex items-center space-x-4 mb-6">
        <div>
          <label className="block text-gray-700 text-md font-medium mb-2">Select Month:</label>
          <input
            type="month"
            className="p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
        </div>

        <button
          onClick={handleDownloadExcel}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex mt-7 items-center"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Statistics Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
          <thead>
            <tr className="bg-gray-100 text-gray-800 uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left">Date</th>
              <th className="py-3 px-6 text-left">Device Type</th>
              <th className="py-3 px-6 text-left">Origin</th>
              <th className="py-3 px-6 text-left">Loads</th>
              <th className="py-3 px-6 text-left">Write Focus</th>
              <th className="py-3 px-6 text-left">Conversations</th>
              <th className="py-3 px-6 text-left">Cotiza</th>
              <th className="py-3 px-6 text-left">WA</th>
              <th className="py-3 px-6 text-left">Finalizado</th>
              <th className="py-3 px-6 text-left">Errors</th>
            </tr>
          </thead>
          <tbody className="text-gray-700 text-sm font-light">
            {stats.map((record, index) => (
              <tr key={`${record.date}-${record.deviceType}-${index}`} className="border-b border-gray-200 hover:bg-gray-100">
                <td className="py-3 px-6">{record.date}</td>
                <td className="py-3 px-6">{record.deviceType}</td>
                <td className="py-3 px-6">{record.origin}</td>
                <td className="py-3 px-6">{record.loads}</td>
                <td className="py-3 px-6">{record.writeFocus}</td>
                <td className="py-3 px-6">{record.conversations}</td>
                <td className="py-3 px-6">{record.cotiza}</td>
                <td className="py-3 px-6">{record.wa}</td>
                <td className="py-3 px-6">{record.finalizado}</td>
                <td className="py-3 px-6">{record.errors}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DailyStats;